import { useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { SidebarSearchTrigger } from "@/shared/components/command-menu/sidebar-search-trigger";
import { isPayrollAdminPath } from "@/shared/components/layout/payroll-sidebar-model";
import { SidebarFooter } from "@/shared/components/layout/sidebar-footer";
import { SidebarNav } from "@/shared/components/layout/sidebar-nav";
import { shouldCloseMobileNavigation } from "@/shared/components/layout/sidebar-slide";
import { WorkspaceSwitcher } from "@/shared/components/layout/sidebar-workspace";
import { Button } from "@/shared/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/shared/components/ui/sheet";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { SidebarCollapsedContext } from "@/shared/hooks/use-sidebar-collapsed";

/// The desktop collapse preference must not shrink the mobile drawer: it is a
/// full off-canvas panel, always expanded.
const FORCE_EXPANDED = { collapsed: false, toggle: () => {}, setCollapsed: () => {} };

/**
 * Mobile navigation (below md). The desktop sidebar is hidden below md, so
 * without this small screens have no way to navigate. A hamburger in the
 * topbar opens an off-canvas Sheet holding the SAME nav pieces as the sidebar
 * (no fork), forced expanded, and it closes itself after a navigation.
 */
export function MobileNav(): ReactNode {
  const [open, setOpen] = useState(false);
  const { workspace } = useActiveWorkspace();
  const { href, pathname } = useRouterState({ select: (state) => state.location });
  const payroll = isPayrollAdminPath(pathname, workspace.slug);
  const previous = useRef({ href, payroll, workspaceId: workspace.id });
  useEffect(() => {
    const before = previous.current;
    previous.current = { href, payroll, workspaceId: workspace.id };
    if (before.href === href && before.workspaceId === workspace.id) return;
    // Keep the drawer visible only for the module's forward/reverse slide.
    if (shouldCloseMobileNavigation(before, { payroll, workspaceId: workspace.id })) setOpen(false);
  }, [href, payroll, workspace.id]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
          <Menu className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[17rem] border-border bg-surface-subtle p-0">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SidebarCollapsedContext.Provider value={FORCE_EXPANDED}>
          <div className="grid h-svh grid-rows-[auto_auto_1fr_auto]">
            <WorkspaceSwitcher />
            <SidebarSearchTrigger />
            <SidebarNav />
            <SidebarFooter />
          </div>
        </SidebarCollapsedContext.Provider>
      </SheetContent>
    </Sheet>
  );
}
